import React, { useRef } from 'react';
import { useRSVPForm } from '../hooks/useRSVPForm.ts';
import { COLORS } from '../constants/colors.ts';
import { SESSION_OPTIONS, RSVP_DEADLINE, PAYMENT_GATEWAY_URL, PAYMENT_REQUIRED_SESSIONS } from '../constants/event.ts';

interface RSVPSectionProps {
  rsvpSectionRef: React.RefObject<HTMLElement | null>;
}

const RSVPSection: React.FC<RSVPSectionProps> = ({ rsvpSectionRef }) => {
  const { wishesData, setWishesData, isSubmitting, submitError, handleSubmit } = useRSVPForm();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Check if payment is required for selected session
  const isPaymentRequired = PAYMENT_REQUIRED_SESSIONS.includes(wishesData.session);

  const handlePayNowClick = () => {
    window.open(PAYMENT_GATEWAY_URL, '_blank', 'noopener,noreferrer');
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type (images only)
      if (!file.type.startsWith('image/')) {
        alert('Please upload an image file (JPG, PNG, etc.)');
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('File size must be less than 5MB');
        return;
      }
      
      setWishesData({
        ...wishesData,
        receiptFile: file,
        receiptFileName: file.name
      });
    }
  };

  const handleRemoveReceipt = () => {
    setWishesData({
      ...wishesData,
      receiptFile: null,
      receiptFileName: ''
    });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const formatPhoneNumber = (value: string) => {
    // Remove all non-numeric characters
    const cleaned = value.replace(/\D/g, '');
    
    // If the number doesn't start with country code and has 10-11 digits, add Malaysia country code
    if (cleaned.length >= 8 && cleaned.length <= 11 && !cleaned.startsWith('60')) {
      return '+6' + cleaned;
    }
    
    // If it already has country code or is too short/long, return as is with + prefix
    if (cleaned.length > 0 && !value.startsWith('+')) {
      return '+' + cleaned;
    }
    
    return value;
  };

  return (
    <section 
      ref={rsvpSectionRef}
      className="py-16 relative overflow-hidden z-[1]"
      style={{ backgroundColor: COLORS.primary }}
    >
      <div className="px-4 relative z-[2]">
        <h3 className="font-awesome-lathusca text-3xl font-bold text-center" style={{ color: COLORS.secondary }}>
          RSVP
        </h3>
        <p className="font-optima text-md text-center mb-2" style={{ color: `${COLORS.secondary}CC` }}>
          Kindly RSVP your attendance before
        </p>
        <p className="font-awesome-lathusca text-2xl font-bold text-center mb-8" style={{ color: COLORS.secondary }}>
          {RSVP_DEADLINE}
        </p>
        
        {/* RSVP Form */}
        <div className="max-w-md mx-auto">
          <form onSubmit={handleSubmit} className="space-y-8">
            <div className="space-y-8">
              <div>
                <input
                  type="text"
                  required
                  placeholder="Your Name"
                  className="w-full px-4 py-3 text-base bg-transparent border-b transition duration-200"
                  style={{
                    borderColor: `${COLORS.secondary}4D`, // 30% opacity
                    color: COLORS.secondary,
                  }}
                  onFocus={(e) => e.target.style.borderColor = COLORS.accent}
                  onBlur={(e) => e.target.style.borderColor = `${COLORS.secondary}4D`}
                  value={wishesData.name}
                  onChange={(e) => setWishesData({...wishesData, name: e.target.value})}
                />
              </div>
              
              <div>
                <input
                  type="tel"
                  placeholder="Phone Number (Optional)"
                  className="w-full px-4 py-3 text-base bg-transparent border-b transition duration-200"
                  style={{
                    borderColor: `${COLORS.secondary}4D`, // 30% opacity
                    color: COLORS.secondary,
                  }}
                  onFocus={(e) => e.target.style.borderColor = COLORS.accent}
                  onBlur={(e) => {
                    e.target.style.borderColor = `${COLORS.secondary}4D`;
                    // Auto-format phone number on blur
                    const formatted = formatPhoneNumber(e.target.value);
                    if (formatted !== e.target.value) {
                      setWishesData({...wishesData, phone: formatted});
                    }
                  }}
                  value={wishesData.phone}
                  onChange={(e) => setWishesData({...wishesData, phone: e.target.value})}
                />
              </div>
              
              <div>
                <input
                  type="email"
                  placeholder="Email Address (Optional)"
                  className="w-full px-4 py-3 text-base bg-transparent border-b transition duration-200"
                  style={{
                    borderColor: `${COLORS.secondary}4D`, // 30% opacity
                    color: COLORS.secondary,
                  }}
                  onFocus={(e) => e.target.style.borderColor = COLORS.accent}
                  onBlur={(e) => e.target.style.borderColor = `${COLORS.secondary}4D`}
                  value={wishesData.email}
                  onChange={(e) => setWishesData({...wishesData, email: e.target.value})}
                />
              </div>
            </div>
              
              <div className="relative">
                <select
                  required
                  className="w-full px-4 py-3 text-base border-b transition duration-200 appearance-none cursor-pointer"
                  style={{
                    backgroundColor: COLORS.primary,
                    borderColor: `${COLORS.secondary}4D`,
                    color: COLORS.secondary,
                  }}
                  onFocus={(e) => e.target.style.borderColor = COLORS.accent}
                  onBlur={(e) => e.target.style.borderColor = `${COLORS.secondary}4D`}
                  value={wishesData.session}
                  onChange={(e) => setWishesData({...wishesData, session: e.target.value})}
                >
                  <option value="" style={{ backgroundColor: COLORS.primary, color: `${COLORS.secondary}80` }}>
                    Select Session
                  </option>
                  {SESSION_OPTIONS.map((option) => (
                    <option key={option} value={option} style={{ backgroundColor: COLORS.primary }}>
                      {option}
                    </option>
                  ))}
                </select>
                <div className="pointer-events-none absolute right-0 top-1/2 -translate-y-1/2 px-2" style={{ color: COLORS.secondary }}>
                  <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                    <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                  </svg>
                </div>
              </div>

            {/* Payment Section - Show only for Afternoon or Both sessions */}
            {isPaymentRequired && (
              <div className="space-y-6 pt-4">
                <div className="text-center">
                  <p className="font-optima text-sm mb-4" style={{ color: `${COLORS.secondary}CC` }}>
                    Payment is required for this session
                  </p>
                  
                  {/* Pay Now Button */}
                  <button
                    type="button"
                    onClick={handlePayNowClick}
                    className="w-full font-bold py-4 rounded-full transition-all duration-150 text-base shadow-md border-2 mb-6 active:scale-95"
                    style={{
                      backgroundColor: COLORS.primary,
                      color: COLORS.secondary,
                      borderColor: COLORS.secondary,
                    }}
                    onTouchStart={(e) => {
                      e.currentTarget.style.backgroundColor = COLORS.secondary;
                      e.currentTarget.style.color = COLORS.primary;
                    }}
                    onTouchEnd={(e) => {
                      e.currentTarget.style.backgroundColor = COLORS.primary;
                      e.currentTarget.style.color = COLORS.secondary;
                    }}
                    onMouseDown={(e) => {
                      e.currentTarget.style.backgroundColor = COLORS.secondary;
                      e.currentTarget.style.color = COLORS.primary;
                    }}
                    onMouseUp={(e) => {
                      e.currentTarget.style.backgroundColor = COLORS.primary;
                      e.currentTarget.style.color = COLORS.secondary;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = COLORS.primary;
                      e.currentTarget.style.color = COLORS.secondary;
                    }}
                  >
                    💳 Pay Now
                  </button>
                </div>

                {/* Receipt Upload Section */}
                <div>
                  <label className="block font-optima text-sm mb-3" style={{ color: `${COLORS.secondary}CC` }}>
                    Upload Payment Receipt <span style={{ color: COLORS.error }}>*</span>
                  </label>
                  
                  {!wishesData.receiptFile ? (
                    <div className="relative">
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleFileChange}
                        className="hidden"
                        id="receipt-upload"
                      />
                      <label
                        htmlFor="receipt-upload"
                        className="flex items-center justify-center w-full px-4 py-4 border-2 border-dashed rounded-lg cursor-pointer transition"
                        style={{
                          borderColor: `${COLORS.secondary}4D`,
                          color: `${COLORS.secondary}99`,
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.borderColor = COLORS.accent;
                          e.currentTarget.style.color = COLORS.accent;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.borderColor = `${COLORS.secondary}4D`;
                          e.currentTarget.style.color = `${COLORS.secondary}99`;
                        }}
                      >
                        <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                        </svg>
                        <span className="font-optima text-sm">Click to upload receipt</span>
                      </label>
                    </div>
                  ) : (
                    <div 
                      className="flex items-center justify-between px-4 py-3 rounded-lg border-2"
                      style={{
                        borderColor: COLORS.accent,
                        backgroundColor: `${COLORS.accent}1A`,
                      }}
                    >
                      <div className="flex items-center flex-1 min-w-0">
                        <svg className="w-5 h-5 mr-2 flex-shrink-0" style={{ color: COLORS.accent }} fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
                        </svg>
                        <span className="font-optima text-sm truncate" style={{ color: COLORS.secondary }}>
                          {wishesData.receiptFileName}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={handleRemoveReceipt}
                        className="ml-2 p-1 rounded-full transition flex-shrink-0"
                        style={{ color: COLORS.secondary }}
                        onMouseEnter={(e) => e.currentTarget.style.color = COLORS.error}
                        onMouseLeave={(e) => e.currentTarget.style.color = COLORS.secondary}
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  )}
                  
                  <p className="font-optima text-xs mt-2" style={{ color: `${COLORS.secondary}66` }}>
                    Accepted formats: JPG, PNG, GIF (Max 5MB)
                  </p>
                </div>
              </div>
            )}
            
            {submitError && (
              <p className="text-sm text-center" style={{ color: COLORS.error }}>
                {submitError}
              </p>
            )}

            {isPaymentRequired && !wishesData.receiptFile && (
              <p className="text-sm text-center" style={{ color: COLORS.error }}>
                Please upload payment receipt to continue
              </p>
            )}

            <button
              type="submit"
              disabled={isSubmitting || (isPaymentRequired && !wishesData.receiptFile)}
              className="w-full font-bold py-4 rounded-full transition-all duration-150 text-lg shadow-md disabled:opacity-60 border-2 active:scale-95"
              style={{
                backgroundColor: COLORS.primary,
                color: COLORS.secondary,
                borderColor: COLORS.secondary,
              }}
              onTouchStart={(e) => {
                if (!isSubmitting) {
                  e.currentTarget.style.backgroundColor = COLORS.secondary;
                  e.currentTarget.style.color = COLORS.primary;
                }
              }}
              onTouchEnd={(e) => {
                if (!isSubmitting) {
                  e.currentTarget.style.backgroundColor = COLORS.primary;
                  e.currentTarget.style.color = COLORS.secondary;
                }
              }}
              onMouseDown={(e) => {
                if (!isSubmitting) {
                  e.currentTarget.style.backgroundColor = COLORS.secondary;
                  e.currentTarget.style.color = COLORS.primary;
                }
              }}
              onMouseUp={(e) => {
                if (!isSubmitting) {
                  e.currentTarget.style.backgroundColor = COLORS.primary;
                  e.currentTarget.style.color = COLORS.secondary;
                }
              }}
              onMouseLeave={(e) => {
                if (!isSubmitting) {
                  e.currentTarget.style.backgroundColor = COLORS.primary;
                  e.currentTarget.style.color = COLORS.secondary;
                }
              }}
            >
              {isSubmitting ? 'Confirming...' : 'Confirm Attendance'}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};

export default RSVPSection;
